import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createSprintRowByResponse,
  createProductBacklogItemRowByResponse,
  isProductBacklogItemRow,
  ProductBacklogItemRow,
  ProductBacklogRow,
  SprintRow,
  isAssociatedWithMultiSprints,
  copyProductBacklogItemRowForAssociatedWithMultiSprints,
  UNSET_SPRINT_ROW,
  isUnsetSprintRowUuid,
} from '../productBacklog'
import {
  createWbsItemDeltaInputByRow,
  isWatcherEdited,
} from '../../../../lib/functions/wbsItem'
import store from '../../../../store'
import {
  doNotRequireSave,
  requireSave,
} from '../../../../store/requiredSaveData'
import { ProductBacklogEntity } from '../../../../domain/entity/ProductBacklogEntity'
import { useProductBacklogRepository } from '../../../../services/adaptors/productBacklogRepositoryAdaptor'
import _ from 'lodash'
import { SprintDetail } from '../../../../lib/functions/sprint'
import {
  SprintProductItemProp,
  UpdateSprintProductItemProps,
  updateSprintProductItems,
} from '../../../../lib/functions/sprintProductItem'
import { UpdateProductBacklogOutput } from '../../../../applications/ports/productBacklogRepository'
import { isTagColumnEdited } from '../../../../lib/functions/tag'

export const useProductBacklogData = (
  projectUuid: string,
  teamUuid: string | undefined,
  sprints: SprintDetail[]
): {
  data: ProductBacklogRow[]
  setData: (newData: ProductBacklogRow[]) => void
  refresh: () => Promise<void>
  save: () => Promise<void>
  refreshSingleRow: (wbsItemUuid: string | undefined) => Promise<void>
  saveSingleRow: (uuid: string) => Promise<void>
  isLoading: boolean
} => {
  const [data, setDataInternal] = useState<ProductBacklogRow[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const originalData = useRef<Map<string, ProductBacklogItemRow>>(new Map())
  const [productBacklog, setProductBacklog] = useState<
    ProductBacklogEntity | undefined
  >()
  const {
    getByTeamUuid: getProductBacklogByTeamUuid,
    update: _updateProductBacklog,
  } = useProductBacklogRepository()

  const setData = useCallback((newData: ProductBacklogRow[]) => {
    setDataInternal(newData)
    store.dispatch(requireSave())
  }, [])

  const fetch = useCallback(async () => {
    if (!teamUuid) {
      setDataInternal([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)

      const productBacklog = await getProductBacklogByTeamUuid(teamUuid)
      setProductBacklog(productBacklog)

      const productBacklogItems = productBacklog?.productBacklogItems || []
      const productBacklogRows: ProductBacklogRow[] = []
      originalData.current.clear()

      // sprintsが未定義の場合は空配列を使用
      const teamSprints = sprints ? sprints.sort((a, b) => a.num - b.num) : []
      const unsetSprintRow: SprintRow = _.cloneDeep(UNSET_SPRINT_ROW)

      const pbiUuidsMapBySprintUuid: Record<string, string[]> = {}
      const pbiMapByUuid: Record<string, ProductBacklogItemRow> = {}
      const addPbiUuid = (sprintUuid: string, pbiUuid: string) => {
        if (!pbiUuidsMapBySprintUuid[sprintUuid]) {
          pbiUuidsMapBySprintUuid[sprintUuid] = []
        }
        pbiUuidsMapBySprintUuid[sprintUuid].push(pbiUuid)
      }

      productBacklogItems.forEach(pbi => {
        let asscociatedSprints = pbi.deliverable.sprints || []
        if (asscociatedSprints.length === 0 && pbi.deliverable.sprint) {
          asscociatedSprints = [pbi.deliverable.sprint]
        }
        if (asscociatedSprints.length === 0) {
          addPbiUuid(unsetSprintRow.uuid, pbi.uuid)
          const pbiRow = createProductBacklogItemRowByResponse(
            unsetSprintRow.uuid,
            pbi
          )
          pbiMapByUuid[pbi.uuid] = pbiRow
          originalData.current.set(pbi.uuid, _.cloneDeep(pbiRow))
          return
        }
        asscociatedSprints.forEach((sprint, index) => {
          addPbiUuid(sprint.uuid, pbi.uuid)
          if (index === 0) {
            const pbiRow = createProductBacklogItemRowByResponse(
              sprint.uuid,
              pbi
            )
            pbiMapByUuid[pbi.uuid] = pbiRow
            originalData.current.set(pbi.uuid, _.cloneDeep(pbiRow))
          }
        })
      })

      // Create pbi rows associated with team sprints.
      teamSprints.forEach(sprint => {
        const sprintRow = createSprintRowByResponse(sprint)
        productBacklogRows.push(sprintRow)

        const pbiUuids = pbiUuidsMapBySprintUuid[sprint.uuid] || []

        // displayOrderでソートしてから行を作成
        const sortedPbiRows: ProductBacklogItemRow[] = []
        pbiUuids.forEach(pbiUuid => {
          const pbiRow = pbiMapByUuid[pbiUuid]
          if (isAssociatedWithMultiSprints(pbiRow)) {
            sortedPbiRows.push(
              copyProductBacklogItemRowForAssociatedWithMultiSprints(
                sprint.uuid,
                pbiRow
              )
            )
          } else {
            sortedPbiRows.push(pbiRow)
          }
        })

        const DEFAULT_DISPLAY_ORDER = 999999
        // displayOrderで昇順ソート
        sortedPbiRows.sort((a, b) => {
          const displayOrderA =
            a.sprintProductItems?.find(spi => spi.sprintUuid === sprint.uuid)
              ?.displayOrder ?? DEFAULT_DISPLAY_ORDER // デフォルト値を大きくして最下位に配置
          const displayOrderB =
            b.sprintProductItems?.find(spi => spi.sprintUuid === sprint.uuid)
              ?.displayOrder ?? DEFAULT_DISPLAY_ORDER // デフォルト値を大きくして最下位に配置
          return displayOrderA - displayOrderB
        })

        productBacklogRows.push(...sortedPbiRows)
      })

      // Create pbi rows unset sprint.
      const pbiUuidsUnsetSprint =
        pbiUuidsMapBySprintUuid[unsetSprintRow.uuid] || []

      productBacklogRows.push(unsetSprintRow)

      pbiUuidsUnsetSprint.forEach(pbiUuid => {
        const pbiRow = pbiMapByUuid[pbiUuid]
        productBacklogRows.push(pbiRow)
      })

      setDataInternal(productBacklogRows)
      store.dispatch(doNotRequireSave())
    } finally {
      setIsLoading(false)
    }
  }, [teamUuid, getProductBacklogByTeamUuid, sprints])

  const refresh = useCallback(async () => {
    await fetch()
  }, [fetch])

  useEffect(() => {
    fetch()
  }, [fetch])

  const updateProductBacklog = useCallback(
    async (
      allPbiRows: ProductBacklogItemRow[],
      editedPbiRows: ProductBacklogItemRow[],
      originalDataMap: Map<string, ProductBacklogItemRow>
    ): Promise<UpdateProductBacklogOutput | undefined> => {
      if (!productBacklog || editedPbiRows.length === 0) {
        return undefined
      }

      const allPbiRowsExcludingDuplicateRow = allPbiRows.filter(
        (v, index, self) =>
          self.findIndex(other => other.originalUuid === v.originalUuid) ===
          index
      )
      const watchers = editedPbiRows
        .filter((row: ProductBacklogItemRow) => {
          const original = originalDataMap.get(row.uuid)?.wbsItem.watchers ?? []
          const watchers = row.wbsItem.watchers ?? []
          return isWatcherEdited(original, watchers)
        })
        .map((row: ProductBacklogItemRow) => ({
          uuid: row.wbsItem.uuid!,
          wbsItemUuid: row.wbsItem.uuid!,
          userUuids: row.wbsItem.watchers?.map(watcher => watcher.uuid) || [],
        }))

      const tags = editedPbiRows
        .filter((row: ProductBacklogItemRow) => {
          const original = originalDataMap.get(row.uuid)?.wbsItem.tags ?? []
          const watchers = row.wbsItem.tags ?? []
          return isTagColumnEdited(original, watchers)
        })
        .map((row: ProductBacklogItemRow) => ({
          uuid: row.wbsItem.uuid!,
          wbsItemUuid: row.wbsItem.uuid!,
          tagUuids: row.wbsItem.tags?.map(tag => tag.uuid) || [],
        }))

      const output = await _updateProductBacklog({
        productBacklog: {
          uuid: productBacklog.uuid,
          lockVersion: productBacklog.lockVersion,
          productBacklogItems: allPbiRowsExcludingDuplicateRow.map(pbiRow => ({
            uuid: pbiRow.originalUuid,
            deliverableUuid: pbiRow.wbsItem.uuid!,
          })),
          editedDeliverables: editedPbiRows.map(pbiRow =>
            createWbsItemDeltaInputByRow(
              originalDataMap.get(pbiRow.originalUuid)?.wbsItem,
              pbiRow.wbsItem
            )
          ),
        },
        watchers,
        tags,
      })
      return output
    },
    [productBacklog, _updateProductBacklog]
  )
  const updateSprints = useCallback(
    (
      editedPbiRows: ProductBacklogItemRow[],
      updateProductBacklogOutput: UpdateProductBacklogOutput,
      currentData: ProductBacklogRow[]
    ) => {
      if (editedPbiRows.length === 0) return
      const wbsItemLockVersionMap: Record<string, number> = {}
      updateProductBacklogOutput.updateDeliverableOutputs.forEach(output => {
        wbsItemLockVersionMap[output.uuid] = output.lockVersion
      })
      const request: UpdateSprintProductItemProps = {
        added: [],
        edited: [],
        deleted: [],
      }

      // 影響を受けるスプリントを収集
      const affectedSprintUuids = new Set<string>()

      // 1. 編集されたアイテムが所属するスプリント
      editedPbiRows.forEach(pbiRow => {
        const wbsItem = pbiRow.wbsItem
        const newSprints = wbsItem.sprints || []
        const oldSprints =
          originalData.current.get(pbiRow.originalUuid)?.wbsItem.sprints || []

        // 新旧両方のスプリントを影響対象に追加
        newSprints.forEach(s => affectedSprintUuids.add(s.uuid))
        oldSprints.forEach(s => affectedSprintUuids.add(s.uuid))
      })

      const reorderedPbiRows: {
        [sprintUuid: string]: ProductBacklogItemRow[]
      } = {}
      currentData.forEach(row => {
        if (
          !isProductBacklogItemRow(row) ||
          !affectedSprintUuids.has(row.treeValue[0])
        ) {
          return
        }
        const parentSprintUuid = row.treeValue[0]
        if (!reorderedPbiRows[parentSprintUuid]) {
          reorderedPbiRows[parentSprintUuid] = []
        }
        reorderedPbiRows[parentSprintUuid].push(row)
      })
      // 影響対象スプリントの全アイテムを処理（兄弟全体の更新）
      affectedSprintUuids.forEach(sprintUuid => {
        // スプリント未設定の場合はスキップ
        if (isUnsetSprintRowUuid(sprintUuid)) {
          return
        }

        // そのスプリントに所属する全アイテムを取得（編集の有無に関わらず）
        const allItemsInSprint = reorderedPbiRows[sprintUuid] || []

        // スプリント内の全アイテムを処理
        allItemsInSprint.forEach((pbiRow, index) => {
          const deliverableUuid = pbiRow.wbsItem.uuid!
          const deliverableLockVersion =
            wbsItemLockVersionMap[deliverableUuid] ||
            pbiRow.wbsItem.lockVersion!
          const estimatedHour =
            pbiRow.sprintProductItems?.find(
              spi => spi.sprintUuid === sprintUuid
            )?.estimatedHour || 0

          // 既存のSprintProductItemかどうかを判定（元データに存在し、uuidが空でないもののみ既存とする）
          const originalPbiRow = originalData.current.get(pbiRow.originalUuid)
          const hasExistingSprintProductItem =
            originalPbiRow?.sprintProductItems?.some(
              spi => spi.sprintUuid === sprintUuid
            ) || false

          const displayOrder = index + 1
          const input: SprintProductItemProp = {
            deliverableUuid,
            deliverableLockVersion,
            sprintUuid,
            estimatedHour,
            displayOrder,
          }
          if (hasExistingSprintProductItem) {
            request.edited.push(input)
          } else {
            request.added.push(input)
          }
        })
      })

      // 削除されたSprintProductItemの処理
      editedPbiRows.forEach(pbiRow => {
        const wbsItem = pbiRow.wbsItem
        const newSprints = wbsItem.sprints || []
        const oldSprints =
          originalData.current.get(pbiRow.originalUuid)?.wbsItem.sprints || []
        const oldSprintUuids = oldSprints.map(s => s.uuid)
        const newSprintUuids = newSprints.map(s => s.uuid)

        const deletedSprintUuids = oldSprintUuids.filter(
          uuid => !newSprintUuids.includes(uuid)
        )
        deletedSprintUuids.forEach(deletedSprintUuid => {
          request.deleted.push({
            deliverableUuid: wbsItem.uuid!,
            deliverableLockVersion:
              wbsItemLockVersionMap[wbsItem.uuid!] || wbsItem.lockVersion!,
            sprintUuid: deletedSprintUuid,
          })
        })
      })
      if (
        request.added.length === 0 &&
        request.edited.length === 0 &&
        request.deleted.length === 0
      ) {
        return
      }
      return updateSprintProductItems(request)
    },
    []
  )

  const save = useCallback(async () => {
    if (isLoading) return
    setIsLoading(true)
    try {
      const editedPbiRows = data.filter(
        v => isProductBacklogItemRow(v) && v.edited
      ) as ProductBacklogItemRow[]
      const output = await updateProductBacklog(
        data.filter(v => isProductBacklogItemRow(v)),
        editedPbiRows,
        originalData.current
      )
      await updateSprints(editedPbiRows, output!, data)
    } finally {
      setIsLoading(false)
    }
  }, [data, isLoading, updateProductBacklog, updateSprints])

  const refreshSingleRow = useCallback(
    async (wbsItemUuid: string | undefined) => {
      // Can not refresh only single row because ProductBacklog has the order.
      await refresh()
    },
    [refresh]
  )
  const saveSingleRow = useCallback(
    async (uuid: string) => {
      // Can not save only single row because ProductBacklog has siblings to need to save all together.
      await save()
    },
    [save]
  )

  return {
    data,
    setData,
    refresh,
    save,
    refreshSingleRow,
    saveSingleRow,
    isLoading,
  }
}
